import { Box, Flex, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure } from '@chakra-ui/react';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import iconUpgrade from 'assets/images/icon_upgrade.png';
import { ICON_CREATED } from 'constants/mixpanel';
import { PaginatedIconSearch, usePaginatedIconSearch } from 'hooks/usePaginatedIconSearch';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { getInitialWidgetPosition } from 'modules/Editor/store';
import { UserType } from 'modules/User';
import { LoadingButton } from 'modules/common/components/Button/LoadingButton';
import { SearchInput } from 'modules/common/components/SearchInput/SearchInput';
import { ReactElement, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconColorOption } from 'types/icon.types';
import { WidgetType } from 'types/widget.types';
import { generateDefaultData } from 'widgets/IconWidget/IconWidget.helpers';
import { generateDefaultData as generateLineIconData } from 'widgets/Lines/LineIconWidget/LineIconWidget.helpers';
import { LineIconWidgetData } from 'widgets/Lines/LineIconWidget/LineIconWidget.types';
import { AllWidgetData, NewWidget } from 'widgets/Widget.types';

import { IconPackView } from './DefaultIconView/IconPackView';
import { IconList } from './IconList';
import { PartialIconMetadata } from './IconWidgetMenu.types';

type OnClickIconWidget =
  | ActionCreatorWithPayload<NewWidget | NewWidget[], string>
  | ((widget: NewWidget | NewWidget[], viewBox: string) => void);

interface IconWidgetMenuProps {
  isIconWidgetMenuActive: boolean;
  onClickIconWidget: OnClickIconWidget;
  colorOptions?: IconColorOption[];
  disableDragDrop?: boolean;
  allowNavFocus?: boolean;
}

export const IconWidgetMenu = ({
  isIconWidgetMenuActive,
  onClickIconWidget,
  colorOptions,
  disableDragDrop,
  allowNavFocus,
}: IconWidgetMenuProps): ReactElement | null => {
  const hasBeenOpenedRef = useRef(false);

  // State to control upgrade modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const onGatedCallback = useRef<(() => void) | null>(null);
  const [upgradePlan, setUpgradePlan] = useState<UserType | null>(null);

  // onGatedCallback is set by individual icons to determine its upgrade type
  // E.g.: Business/Premium icons
  const setUpgradeCallback = useCallback((callback: () => void, upgradePlan: UserType) => {
    onGatedCallback.current = callback;
    setUpgradePlan(upgradePlan);
  }, []);

  // persist icon menu state when switching sidebar tabs
  const display = isIconWidgetMenuActive ? '' : 'none';
  const paginatedIconSearch = usePaginatedIconSearch({ colorOptions });
  const { searchIcons, clearSearch, query } = paginatedIconSearch;

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.iconsMenu' });

  const createWidgetData = (iconId: string, viewBox: string, mono?: boolean, iconMetadata?: PartialIconMetadata) => {
    const { top, left } = getInitialWidgetPosition();

    const iconConfig = iconMetadata ? { iconMetadata: { isMono: !!mono, viewBox, ...iconMetadata } } : {};
    const widgetData = generateDefaultData(iconId, viewBox, top, left, mono, iconConfig);
    return widgetData;
  };
  const handleClickIconWidget = (
    iconId: string,
    viewBox: string,
    mono?: boolean,
    iconMetadata?: PartialIconMetadata,
  ): void => {
    const widget = createWidgetData(iconId, viewBox, mono, iconMetadata);

    onClickIconWidget(widget, viewBox);

    Mixpanel.track(ICON_CREATED.eventName, {
      from: ICON_CREATED.from.Pack,
      icon_id: iconId,
    });
  };

  // The callback that is called when upgrade in the modal is clicked, dependant on
  // individual icon to know the feature name
  const onClickUpgrade = useCallback(() => {
    // should be set by individual icons
    onGatedCallback?.current?.();
  }, []);

  // Only render if the menu has been opened before
  // Otherwise, may cause performance issues loading all the icons on page load
  if (isIconWidgetMenuActive && !hasBeenOpenedRef.current) {
    hasBeenOpenedRef.current = true;
  }

  if (!hasBeenOpenedRef.current) {
    return null;
  }

  return (
    <Box display={display} h='full'>
      <CommonGatedUpgradeModal
        isOpen={isOpen}
        onClose={onClose}
        onClickUpgrade={onClickUpgrade}
        upgradePlan={upgradePlan}
        modalImg={iconUpgrade}
        modalType={ModalUpgradeTypeEnum.IconUpgrade}
      />
      <Flex direction='column' h='full'>
        <Box p='4' display={'flex'} justifyContent={'center'}>
          <SearchInput
            onSearch={searchIcons}
            clearSearch={clearSearch}
            label={t('searchPlaceholder')}
            clearButtonLabel={t('clearButtonLabel')}
            focusOnMenuOpen={true}
            isVisible={isIconWidgetMenuActive}
          />
        </Box>
        {query && (
          <IconSearch
            onClickIconWidget={onClickIconWidget}
            paginatedIconSearch={paginatedIconSearch}
            setUpgradeCallback={setUpgradeCallback}
            onOpenModal={onOpen}
            allowNavFocus={allowNavFocus}
          />
        )}
        <IconPackView
          colorOptions={colorOptions}
          onClickIconWidget={handleClickIconWidget}
          show={!query}
          setUpgradeCallback={setUpgradeCallback}
          onOpenModal={onOpen}
          createWidgetData={createWidgetData}
          disableDragDrop={disableDragDrop}
          allowNavFocus={allowNavFocus}
        />
      </Flex>
    </Box>
  );
};

interface IconSearchProps {
  paginatedIconSearch: PaginatedIconSearch;
  onClickIconWidget: OnClickIconWidget;
  setUpgradeCallback?: (callback: () => void, upgradePlan: UserType) => void;
  onOpenModal: () => void;
  allowNavFocus?: boolean;
}

const IconSearch = ({
  paginatedIconSearch,
  onClickIconWidget,
  setUpgradeCallback,
  onOpenModal,
  allowNavFocus,
}: IconSearchProps) => {
  const {
    hasMore,
    isLoadingInitialData,
    isLoadingMore,
    selectedColorOption,
    colorOptions,
    setSelectedColorOption,
    icons,
    onShowMore,
    query,
    hasNoResultsFound,
  } = paginatedIconSearch;
  const [tabIndex, setTabIndex] = useState(0);

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.iconsMenu' });

  const createWidgetData = (
    iconId: string,
    viewBox: string,
    mono?: boolean,
    iconMetadata?: { iconPath?: string; isIllustration?: boolean },
  ) => {
    const { top, left } = getInitialWidgetPosition();
    const iconConfig = iconMetadata ? { iconMetadata: { isMono: !!mono, viewBox, ...iconMetadata } } : {};
    const widgetData = generateDefaultData(iconId, viewBox, top, left, mono, iconConfig);
    return widgetData;
  };

  const handleClickIconWidget = (
    iconId: string,
    viewBox: string,
    mono?: boolean,
    iconMetadata?: { iconPath?: string; isIllustration?: boolean },
  ): void => {
    const widget = createWidgetData(iconId, viewBox, mono, iconMetadata);
    onClickIconWidget(widget, viewBox);

    Mixpanel.track(ICON_CREATED.eventName, {
      from: ICON_CREATED.from.Search,
      icon_id: iconId,
    });
  };

  const handleAddNonIconWidget = (type: WidgetType, widgetData: Partial<AllWidgetData>) => {
    const { top, left } = getInitialWidgetPosition();
    switch (type) {
      case WidgetType.LineIcon:
        const lineWidgetData = widgetData as LineIconWidgetData;
        onClickIconWidget(
          generateLineIconData(lineWidgetData.type, {
            ...lineWidgetData,
            topPx: top,
            leftPx: left,
          }),
          '',
        );
        break;
      default:
        break;
    }
  };

  const hasIcons = !!(icons && icons.length);
  const showStyleTabs = !!((hasIcons || selectedColorOption !== IconColorOption.All) && query);

  const handleTabChange = (index: number) => {
    setSelectedColorOption(colorOptions[index]);
    setTabIndex(index);
  };

  const colorOptionsIdx = colorOptions.indexOf(selectedColorOption);

  // reset tab index if it is out of bounds
  if (tabIndex >= colorOptions.length) setTabIndex(0);
  else if (tabIndex !== colorOptionsIdx) setTabIndex(colorOptionsIdx);

  const boxShadow = allowNavFocus ? 'inset var(--vg-shadows-outline)' : 'none';

  return (
    <Flex direction='column' h='full' overflow={'auto'}>
      {showStyleTabs ? (
        <Tabs onChange={handleTabChange} index={tabIndex} display='flex' flexDir='column' flex='1' overflow='hidden'>
          <TabList h={10} data-testid='icon-style-tab-menu'>
            {colorOptions.map((colorOption: IconColorOption) => (
              <Tab
                key={colorOption}
                fontSize='sm'
                fontWeight='semibold'
                textTransform='capitalize'
                _focus={{ boxShadow }}
              >
                {t(`colorOption.${colorOption}` as keyof typeof t)}
              </Tab>
            ))}
          </TabList>
          <TabPanels flex='1' overflow='auto'>
            {colorOptions.map((colorOption: IconColorOption) => (
              <TabPanel
                key={colorOption}
                // chakra-ui defaults TabPanel padding to {4}
                // reduce left padding to {2} to handle certain scrollbars breaking column layout
                pl={2}
                pr='0'
                style={{
                  boxShadow,
                }}
              >
                <IconList
                  icons={icons}
                  onClickIconWidget={handleClickIconWidget}
                  setUpgradeCallback={setUpgradeCallback}
                  onOpenModal={onOpenModal}
                  createWidgetData={createWidgetData}
                  onClickNonIconWidget={handleAddNonIconWidget}
                />
                {/* Show more button */}
                {!isLoadingInitialData && hasMore && (
                  <Flex justify='center'>
                    <LoadingButton isLoading={isLoadingMore} onClick={onShowMore} colorScheme='green' mt={4} size='sm'>
                      {t('showMore')}
                    </LoadingButton>
                  </Flex>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      ) : null}

      {/* Loading indicator */}
      {isLoadingInitialData && (
        <Spinner data-testid='icon-widgetmenu-spinner' alignSelf='center' color='var(--vg-colors-brand-500)' />
      )}

      {/* Error handler: NotFound */}
      {hasNoResultsFound && <NoIconsFound query={query} />}
    </Flex>
  );
};

interface NoIconsFoundProps {
  query: string;
}

const NoIconsFound = ({ query }: NoIconsFoundProps): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.iconsMenu' });
  return <Text p='4'>{t('noIconsFound', { query })}</Text>;
};
